import type {AuthorSettings} from '@github-ui/commit-attribution'
import {CommitAttribution as UiCommitAttribution} from '@github-ui/commit-attribution'
import type {RepositoryNWO} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {RelativeTime} from '@primer/react'
import type {PropsWithChildren} from 'react'

import {useFindDeferredCommitData} from '../contexts/DeferredCommitDataContext'
import type {BaseCommit} from '../shared/types'

function renderCommitTime(isSSR: boolean, datetime?: string) {
  return isSSR ? (
    <LoadingSkeleton variant="rounded" className="d-none d-sm-flex ml-1" width="60px" />
  ) : (
    <RelativeTime className="pl-1" datetime={datetime} />
  )
}

interface CommitAttributionProps {
  commit: BaseCommit
  repo: RepositoryNWO
  settings?: Partial<AuthorSettings>
  textVariant?: React.ComponentProps<typeof UiCommitAttribution>['textVariant']
}

export function CommitAttribution({
  commit,
  repo,
  children,
  settings,
  textVariant,
}: PropsWithChildren<CommitAttributionProps>) {
  const deferredData = useFindDeferredCommitData(commit.oid)
  const {isServer: isSSR} = useRuntimeEnvironment()
  const showPusherOnCommitDetail = isFeatureEnabled('authentic_contributions_frontend')
  const pusher = showPusherOnCommitDetail ? commit.pusher : undefined

  return (
    <UiCommitAttribution
      authors={commit.authors}
      committer={commit.committer}
      committedDate={commit.committedDate}
      committerAttribution={commit.committerAttribution}
      onBehalfOf={deferredData?.onBehalfOf}
      repo={repo}
      includeVerbs
      authorSettings={{
        fontWeight: 'var(--base-text-weight-normal)',
        fontColor: 'var(--fgColor-muted)',
        avatarSize: 16,
        ...settings,
      }}
      textVariant={textVariant}
      pusher={pusher}
      pushedDate={commit.pushedDate}
      pushedTime={pusher && commit.pushedDate && renderCommitTime(isSSR, commit.pushedDate)}
      showCommitIcon={showPusherOnCommitDetail}
    >
      {renderCommitTime(isSSR, commit.committedDate)}
      {children}
    </UiCommitAttribution>
  )
}
